import { queryValues } from '../utils/queryValues.js'

export const COLLECTION_SORT_OPTIONS = [
	{
		value: 'added',
		labelKey: 'collection.sortAdded',
	},
	{
		value: 'artist',
		labelKey: 'collection.sortArtist',
	},
	{
		value: 'title',
		labelKey: 'collection.sortTitle',
	},
	{
		value: 'year',
		labelKey: 'collection.sortReleaseYear',
	},
	{
		value: 'masterYear',
		labelKey: 'collection.sortMasterYear',
	},
	{
		value: 'listened',
		labelKey: 'collection.sortListened',
	},
	{
		value: 'rating',
		labelKey: 'collection.sortRating',
	},
] as const

export const SORT_ORDERS = ['asc', 'desc'] as const

export const STATUS_FILTER_OPTIONS = [
	{ value: 'all', labelKey: 'collection.historyAll' },
	{ value: 'listened', labelKey: 'collection.historyOnly' },
	{ value: 'notListened', labelKey: 'collection.historyNotListened' },
	{ value: 'hidden', labelKey: 'collection.historyHidden' },
] as const

export const MEDIA_FILTER_OPTIONS = [
	{ value: 'all', labelKey: 'collection.mediaAll' },
	{ value: 'Vinyl', labelKey: 'collection.mediaVinyl' },
	{ value: 'CD', labelKey: 'collection.mediaCD' },
	{ value: 'Cassette', labelKey: 'collection.mediaCassette' },
	{ value: 'Single', labelKey: 'collection.mediaSingle' },
	{ value: 'Box Set', labelKey: 'collection.mediaBoxSet' },
] as const

export const RATING_FILTER_OPTIONS = [0, 1, 2, 3, 4, 5] as const

export const HISTORY_SORT_OPTIONS = [
	{ value: 'history-asc', labelKey: 'historyPage.dateAsc' },
	{ value: 'history-desc', labelKey: 'historyPage.dateDesc' },
] as const

export const DEFAULT_COLLECTION_SORT =
	'titleAsc' satisfies CollectionSortFilterOptions
export const DEFAULT_STATUS_FILTER = 'all' satisfies StatusFilterOptions
export const DEFAULT_MEDIA_FILTER = 'all' satisfies MediaFilterOptions

export type CollectionSort = (typeof COLLECTION_SORT_OPTIONS)[number]['value']
export type SortOrder = (typeof SORT_ORDERS)[number]
export type CollectionSortFilterOptions =
	`${CollectionSort}${Capitalize<SortOrder>}`
export type StatusFilterOptions =
	(typeof STATUS_FILTER_OPTIONS)[number]['value']
export type MediaFilterOptions = (typeof MEDIA_FILTER_OPTIONS)[number]['value']
export type RatingFilterOptions = (typeof RATING_FILTER_OPTIONS)[number]
export type HistorySortFilterOptions =
	(typeof HISTORY_SORT_OPTIONS)[number]['value']

export type CollectionSortSpec = {
	sort: CollectionSort
	sortOrder: SortOrder
}

export const COLLECTION_SORT_FILTER_OPTIONS = COLLECTION_SORT_OPTIONS.flatMap(
	({ value }) =>
		SORT_ORDERS.map((order) => getCollectionSortFilterOption(value, order)),
)
const STATUS_FILTER_VALUES = STATUS_FILTER_OPTIONS.map((option) => option.value)
const MEDIA_FILTER_VALUES = MEDIA_FILTER_OPTIONS.map((option) => option.value)

/** Checks whether an unknown value is one of the supported collection sort option IDs. */
export function isCollectionSortFilterOption(
	value: unknown,
): value is CollectionSortFilterOptions {
	return (
		typeof value === 'string' &&
		COLLECTION_SORT_FILTER_OPTIONS.includes(
			value as CollectionSortFilterOptions,
		)
	)
}

/** Checks whether an unknown value is one of the supported collection status filters. */
export function isStatusFilterOption(
	value: unknown,
): value is StatusFilterOptions {
	return (
		typeof value === 'string' &&
		STATUS_FILTER_VALUES.includes(value as StatusFilterOptions)
	)
}

/** Checks whether an unknown value is one of the supported media filters. */
export function isMediaFilterOption(
	value: unknown,
): value is MediaFilterOptions {
	return (
		typeof value === 'string' &&
		MEDIA_FILTER_VALUES.includes(value as MediaFilterOptions)
	)
}

/** Checks whether a value is an unrated or one-to-five-star filter value. */
export function isRatingFilterOption(
	value: unknown,
): value is RatingFilterOptions {
	return (
		typeof value === 'number' &&
		Number.isInteger(value) &&
		RATING_FILTER_OPTIONS.includes(value as RatingFilterOptions)
	)
}

/** Normalizes an actual one-to-five-star personal rating. */
export function normalizeReleaseRating(value: unknown): number | null {
	const rating = Number(value)
	return Number.isInteger(rating) && rating >= 1 && rating <= 5
		? rating
		: null
}

/** Converts a UI sort option ID into the normalized DB sort field and direction. */
export function getCollectionSortSpec(value: unknown): CollectionSortSpec {
	const normalized = normalizeCollectionSort(value)
	const sortOrder = normalized.endsWith('Desc') ? 'desc' : 'asc'
	const suffix = sortOrder === 'desc' ? 'Desc' : 'Asc'
	return {
		sort: normalized.slice(0, -suffix.length) as CollectionSort,
		sortOrder,
	}
}

/** Combines a collection sort field and direction for storage and query params. */
export function getCollectionSortFilterOption(
	sort: CollectionSort,
	sortOrder: SortOrder,
): CollectionSortFilterOptions {
	return `${sort}${sortOrder === 'asc' ? 'Asc' : 'Desc'}`
}

/** Returns a valid collection sort option, falling back to the default sort. */
export function normalizeCollectionSort(
	value: unknown,
): CollectionSortFilterOptions {
	return isCollectionSortFilterOption(value) ? value : DEFAULT_COLLECTION_SORT
}

/** Returns a valid status filter, falling back to the default status. */
export function normalizeStatusFilter(value: unknown): StatusFilterOptions {
	return isStatusFilterOption(value) ? value : DEFAULT_STATUS_FILTER
}

/** Returns valid status filters for repeated query params, falling back to the default status. */
export function normalizeStatusFilters(
	value: unknown,
): StatusFilterOptions | StatusFilterOptions[] {
	const values = queryValues(value).filter(isStatusFilterOption)
	const activeValues = values.filter((item) => item !== DEFAULT_STATUS_FILTER)
	if (!activeValues.length) return DEFAULT_STATUS_FILTER
	return activeValues.length === 1 ? activeValues[0] : activeValues
}

/** Returns a valid media filter, falling back to the default media value. */
export function normalizeMediaFilter(value: unknown): MediaFilterOptions {
	return isMediaFilterOption(value) ? value : DEFAULT_MEDIA_FILTER
}

/** Returns valid media filters for repeated query params, falling back to the default media value. */
export function normalizeMediaFilters(
	value: unknown,
): MediaFilterOptions | MediaFilterOptions[] {
	const values = queryValues(value).filter(isMediaFilterOption)
	const activeValues = values.filter((item) => item !== DEFAULT_MEDIA_FILTER)
	if (!activeValues.length) return DEFAULT_MEDIA_FILTER
	return activeValues.length === 1 ? activeValues[0] : activeValues
}

/** Returns valid exact rating filters, where zero represents unrated releases. */
export function normalizeRatingFilters(
	value: unknown,
): RatingFilterOptions | RatingFilterOptions[] | undefined {
	const rawValues: unknown[] = Array.isArray(value) ? value : [value]
	const values = rawValues
		.flatMap<string | number>((item) => {
			if (typeof item === 'number') return [item]
			if (typeof item !== 'string') return []
			return item.split(',').map((entry) => entry.trim())
		})
		.filter((item) => item !== '')
		.map(Number)
		.filter(isRatingFilterOption)
	const uniqueValues = [...new Set(values)]
	if (!uniqueValues.length) return undefined
	return uniqueValues.length === 1 ? uniqueValues[0] : uniqueValues
}
